import { CheckIcon, EmptyState } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useDeleteTeachingBlockExclusionsMutation } from '../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../utils/toast';
import { ExclusionTimeframeItem } from '../../ExclusionTimeframe/ExclusionTimeframeItem';
import { useTeachingBlockVersionData } from '../hooks/useTeachingBlockVersionData';

export const Exclusions = ({
  versionUuid,
  uuids,
  onEdit,
  entityType,
}: {
  versionUuid: string;
  uuids: string[];
  onEdit: (uuid: string) => void;
  entityType: 'subject' | 'room' | 'class' | 'teacher';
}) => {
  const { t } = useTranslation();

  const context = useMemorizedCacheTag('TEACHING_BLOCK_AVAILABILITY_EXCLUSION');

  // Ausschlüsse der Version aus dem Lesemodell (nach Beginn sortiert); früher lud die Liste die
  // Ausschlüsse aller Versionen der Schule.
  const { versionData } = useTeachingBlockVersionData(versionUuid);

  const [, remove] = useDeleteTeachingBlockExclusionsMutation();

  const exclusions =
    versionData?.exclusions.filter((e) => {
      return uuids.includes(e.uuid);
    }) ?? [];

  const { confirm, ConfirmationDialog } = useConfirm();

  async function onDelete(uuid: string) {
    const confirmed = await confirm({ message: t('teachingBlockVersion.exclusion.deleteConfirm') });
    if (confirmed) {
      const response = await remove({ uuids: [uuid] }, context);
      if (response.error) {
        showUserErrorToast({ error: response.error });
      } else {
        showSuccessDeleteToast();
      }
    }
  }

  const typeName = (_entityType: 'subject' | 'room' | 'class' | 'teacher') => {
    switch (_entityType) {
      case 'subject':
        return t('subject.title', { count: 1 });
      case 'room':
        return t('rooms.title', { count: 1 });
      case 'class':
        return t('classes.title', { count: 1 });
      case 'teacher':
        return t('persons.title', { count: 1 });
    }
  };
  return (
    <>
      {exclusions.length === 0 ? (
        <EmptyState
          icon={<CheckIcon className='bordered' />}
          size='small'
          iconColor='var(--color-success)'
          title={t('teachingBlockVersion.exclusion.isFullyAvailable', {
            type: typeName(entityType),
          })}
          subtitle={t('teachingBlockVersion.exclusion.isFullyAvailableHint')}
          padding='xl'
        />
      ) : (
        <>
          {exclusions.map((item) => {
            return <ExclusionTimeframeItem key={item.uuid} item={item} onEdit={onEdit} onDelete={onDelete} />;
          })}
        </>
      )}
      <ConfirmationDialog />
    </>
  );
};
